import { ChangeEvent, forwardRef, KeyboardEvent, useState } from "react";
import { Box } from "@mui/material";
import strings from "global/constants/StringConstants";
import { fillCopy } from "helpers/methods";
import styles from "../AiWebForm.styles";
import { MAX_MESSAGE_LENGTH, isValidMessage } from "../AiWebFormTypesAndValidation";
import { ArrowUpGlyph } from "./AiWebFormIcons";

const copy = strings.aiWebForm;

interface Props {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  /** A turn is running, or the page cannot send yet: typing stays allowed, sending does not. */
  sendDisabled: boolean;
  /** The sender may not change this design at all. */
  readOnly?: boolean;
}

/** The message box: Enter sends, Shift+Enter adds a line. */
export const AiComposer = forwardRef<HTMLTextAreaElement, Props>(
  ({ value, onChange, onSend, sendDisabled, readOnly }, ref) => {
    const [focused, setFocused] = useState(false);
    const canSend = !sendDisabled && !readOnly && isValidMessage(value);

    const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
        event.preventDefault();
        if (canSend) onSend();
      }
    };

    return (
      <Box sx={styles.composerWrap}>
        <Box sx={styles.composer(focused)}>
          <Box
            component="textarea"
            ref={ref}
            rows={2}
            sx={styles.textarea}
            value={value}
            maxLength={MAX_MESSAGE_LENGTH}
            placeholder={copy.composerPlaceholder}
            aria-label={copy.composerLabel}
            disabled={readOnly}
            onChange={(event: ChangeEvent<HTMLTextAreaElement>) => onChange(event.target.value)}
            onKeyDown={onKeyDown}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
          />
          <Box sx={styles.composerBar}>
            <Box component="span" sx={styles.counter} aria-live="off">
              {fillCopy(copy.counter, { count: value.length, max: MAX_MESSAGE_LENGTH })}
            </Box>
            <Box
              component="button"
              type="button"
              sx={styles.sendButton}
              onClick={onSend}
              disabled={!canSend}
              aria-label={copy.send}
            >
              <ArrowUpGlyph />
            </Box>
          </Box>
        </Box>
      </Box>
    );
  },
);

AiComposer.displayName = "AiComposer";
